import React, { useState } from 'react';
import { purchaseMemorySlots } from '../../utils/memoryCapacityApi';
import { FEATURE_PRICING } from '../../config/featurePricing';
import { logger } from '../../utils/logger';
import './MemoryLimitModal.css';

/**
 * Modal shown when user has reached their memory limit
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Handler to close modal
 * @param {Object} props.user - Firebase user object
 * @param {number} props.currentCount - Current number of memories
 * @param {number} props.totalCapacity - Total capacity (free + purchased)
 * @param {number|null} props.currentCredits - User's current credit balance
 * @param {Function} props.onPurchaseSuccess - Called after successful purchase
 * @param {Function} props.onBuyCredits - Handler to open credits modal
 */
export default function MemoryLimitModal({
  isOpen,
  onClose,
  user,
  currentCount,
  totalCapacity,
  currentCredits,
  onPurchaseSuccess,
  onBuyCredits,
}) {
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const slotPacks = FEATURE_PRICING.memories.slotPacks;

  const handlePurchase = async () => {
    if (!user || !selectedPack) return;

    setLoading(true);
    setError(null);

    try {
      await purchaseMemorySlots(user, selectedPack.slots, selectedPack.credits);
      onPurchaseSuccess?.();
      onClose();
    } catch (err) {
      logger.error('Purchase error:', err);
      setError(err.message || 'Failed to purchase slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleBuyCredits = () => {
    onClose();
    onBuyCredits?.();
  };

  if (!isOpen) return null;

  const credits = currentCredits ?? 0;

  return (
    <div className="memory-limit-overlay" onClick={handleOverlayClick}>
      <div className="memory-limit-modal">
        <button
          className="memory-limit-close"
          onClick={onClose}
          aria-label="Close"
          disabled={loading}
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div className="memory-limit-header">
          <h2>Memory Limit Reached</h2>
          <p className="memory-limit-status">
            You've used <strong>{currentCount}/{totalCapacity}</strong> memory slots
          </p>
          <p className="memory-limit-credits">
            Your gems: <strong>{credits}</strong>
          </p>
        </div>

        {error && (
          <div className="memory-limit-error">
            {error}
          </div>
        )}

        <div className="memory-limit-section">
          <p className="memory-limit-section-title">Get more memory slots:</p>
          <div className="memory-limit-packs">
            {slotPacks.map((pack, index) => {
              const canAfford = credits >= pack.credits;
              const isSelected = selectedPack?.slots === pack.slots;
              const perSlot = Math.round(pack.credits / pack.slots);

              return (
                <button
                  key={pack.slots}
                  className={`memory-limit-pack ${isSelected ? 'selected' : ''} ${!canAfford ? 'disabled' : ''}`}
                  onClick={() => canAfford && setSelectedPack(pack)}
                  disabled={loading || !canAfford}
                >
                  {index === 1 && <span className="memory-limit-pack-badge">Best Value</span>}
                  <span className="memory-limit-pack-slots">+{pack.slots}</span>
                  <span className="memory-limit-pack-label">
                    {pack.slots === 1 ? 'slot' : 'slots'}
                  </span>
                  <span className="memory-limit-pack-cost">{pack.credits} gems</span>
                  <span className="memory-limit-pack-unit">
                    {perSlot} gems / slot
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="memory-limit-actions">
          {selectedPack ? (
            <button
              className="memory-limit-action-primary"
              onClick={handlePurchase}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="memory-limit-spinner"></span>
                  Processing...
                </>
              ) : (
                `Purchase ${selectedPack.slots} slot${selectedPack.slots > 1 ? 's' : ''}`
              )}
            </button>
          ) : (
            <button
              className="memory-limit-action-secondary"
              onClick={handleBuyCredits}
              disabled={loading}
            >
              Buy More Gems
            </button>
          )}
        </div>

        <p className="memory-limit-tip">
          Tip: Delete old memories to free up slots!
        </p>
      </div>
    </div>
  );
}
